import { Warning } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import {
  GenSnackBar,
  GenConfirmationDialog,
} from '@pinnacleautomotive/clarity-components';
import isEqual from 'lodash.isequal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../redux/rtk/api';
import {
  useDeleteUserMutation,
  useEditUserMutation,
  useGetUsersQuery,
  useInviteUserMutation,
  useResendInvitationMutation,
} from '../../redux/rtk/users.services';
import { currentUserCompanyId } from '../../redux/selectors/userDataSelectors';
import Filters from './components/Filters';
import NewUserButton from './components/NewUserButton';
import NewUserDialog from './components/NewUserDialog';
import UsersTable from './components/UsersTable';

const MESSAGE_BY_TOKEN_STATUS = {
  USER_ALREADY_EXISTS: {
    title: 'USER_ALREADY_EXISTS.title',
    message: 'USER_ALREADY_EXISTS.message',
  },
  UNEXPECTED_ERROR: {
    title: 'invitationSentError',
  },
};

export default function Users() {
  const { t } = useTranslation('users');
  const dispatch = useDispatch();
  const companyId = useSelector(currentUserCompanyId);
  const [filters, setFilters] = useState({
    level: [companyId],
    searchQuery: '',
  });
  const [page, setPage] = useState(1);
  const [userToDelete, setUserToDelete] = useState(null);
  const [openNewUserModal, setOpenNewUserModal] = useState(false);
  const [userInEditing, setUserInEditing] = useState(null);
  const [successSnackBarMessage, setSuccessSnackBarMessage] = useState('');
  const [errorSnackBarMessage, setErrorSnackBarMessage] = useState('');
  const [errorSnackBarMessageDetail, setErrorSnackBarMessageDetail] =
    useState('');
  const [inviteNewUser, { isLoading: isLoadingNewUser }] =
    useInviteUserMutation();
  const [editUser, { isLoading: isLoadingEditUser }] = useEditUserMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [deleteUser, { isLoading: isLoadingDeleteUser }] =
    useDeleteUserMutation();

  const { data, isLoading, isFetching } = useGetUsersQuery({
    page,
    ...filters,
  });

  const onLoadMore = () => {
    setPage(page + 1);
  };

  const isLoadingOrFetching = isLoading || isFetching;

  const changeFiltersHandler = newFilters => {
    if (isEqual(filters, newFilters)) return;

    setPage(1);
    setFilters(newFilters);
  };

  const openNewUserModalHandler = () => {
    setOpenNewUserModal(true);
  };

  const sendNewUserInvitation = async newUser => {
    try {
      const newUserSaved = await inviteNewUser(newUser).unwrap();
      dispatch(
        api.util.updateQueryData('getUsers', filters, previousData => {
          previousData.users.unshift(newUserSaved);
        })
      );
      setSuccessSnackBarMessage(t('invitationSent'));
      setOpenNewUserModal(false);
    } catch (error) {
      const token =
        MESSAGE_BY_TOKEN_STATUS[error?.data?.token] ||
        MESSAGE_BY_TOKEN_STATUS.UNEXPECTED_ERROR;

      setErrorSnackBarMessage(
        t(token.title, {
          companyName: error?.data?.data?.existingUser?.companyName,
        })
      );
      setErrorSnackBarMessageDetail(t(token.message, { defaultValue: null }));
    }
  };

  const updateUser = async user => {
    try {
      const dataEdited = await editUser({
        data: user,
        personId: userInEditing.personId,
      }).unwrap();

      dispatch(
        api.util.updateQueryData('getUsers', filters, previousData => {
          const userUpserted = previousData.users.find(
            subUser => subUser.personId === userInEditing.personId
          );

          Object.assign(userUpserted, dataEdited);
        })
      );
      setSuccessSnackBarMessage(t('userEdited'));
      setUserInEditing(null);
    } catch (err) {
      const errorMessage =
        err?.data?.token === 'INACTIVE_COMPANY'
          ? t('userEditDeactivatedCompanyError')
          : t('userEditError');

      setErrorSnackBarMessage(errorMessage);
    }
  };

  const resendInvitationHandler = async user => {
    try {
      await resendInvitation(user).unwrap();
      setSuccessSnackBarMessage(t('invitationSent'));
    } catch {
      setErrorSnackBarMessage(t('sendError'));
    }
  };

  const deleteUserHandler = async () => {
    try {
      await deleteUser(userToDelete).unwrap();
      dispatch(
        api.util.updateQueryData('getUsers', filters, previousData => {
          const newUsers = previousData.users.filter(
            subUser => subUser.personId !== userToDelete.personId
          );

          previousData.users = newUsers;
        })
      );
      setUserToDelete(null);
      setSuccessSnackBarMessage(t('deleteSuccess'));
    } catch {
      setErrorSnackBarMessage(t('deleteError'));
    }
  };

  const users = isLoadingOrFetching && page === 1 ? null : data;

  return (
    <>
      <GenConfirmationDialog
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onRefuse={() => setUserToDelete(null)}
        onConfirm={deleteUserHandler}
        titleIcon={Warning}
        titleIconColor={'warning'}
        isLoading={isLoadingDeleteUser}
        title={t('deleteUser')}
        subTitle={t('deleteUserSubTitle')}
        closeText={t('noWait')}
        confirmText={t('yesDelete')}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap={1}
        >
          <Typography variant="overline">{t('delete')}:</Typography>
          <Typography variant="subtitle2">
            {[userToDelete?.firstName, userToDelete?.lastName]
              .filter(x => x)
              .join(' ') || userToDelete?.email}
          </Typography>
        </Stack>
      </GenConfirmationDialog>
      <GenSnackBar
        title={successSnackBarMessage}
        open={!!successSnackBarMessage}
        onClose={() => setSuccessSnackBarMessage('')}
      />
      <GenSnackBar
        title={errorSnackBarMessage}
        message={errorSnackBarMessageDetail}
        open={!!errorSnackBarMessage}
        type="error"
        autoHideDuration={null}
        onClose={() => {
          setErrorSnackBarMessage('');
          setErrorSnackBarMessageDetail(null);
        }}
      />
      <NewUserDialog
        open={openNewUserModal}
        key={successSnackBarMessage}
        onClose={() => setOpenNewUserModal(false)}
        onSave={sendNewUserInvitation}
        isLoading={isLoadingNewUser}
      />
      <NewUserDialog
        open={!!userInEditing}
        user={userInEditing}
        onClose={() => setUserInEditing(null)}
        onSave={updateUser}
        isLoading={isLoadingEditUser}
      />
      <Stack py={3.5} px={3} gap={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack gap={1}>
            <Typography variant="h6">{t('companyUsers')}</Typography>
            <Typography variant="body1" color="text.secondary">
              {t('manageAllYourPeople')}
            </Typography>
          </Stack>
          <NewUserButton onClick={openNewUserModalHandler} />
        </Stack>
        <Stack gap={3}>
          <Filters onChange={changeFiltersHandler} />
          <UsersTable
            onLoadMore={onLoadMore}
            data={users}
            isLoadingOrFetching={isLoadingOrFetching}
            onEdit={user => setUserInEditing(user)}
            onResend={resendInvitationHandler}
            onDelete={user => setUserToDelete(user)}
          />
        </Stack>
      </Stack>
    </>
  );
}
